import React from "react";
import { useIntl } from "react-intl";
import { Member, UpdateOwner, MemberPageParam } from "@/model";
import { hasAdminPermission } from "@/util/permissionUtil";
import { UTCToLocalString } from "@/util/dateUtil";
import { useRole } from "@/query/role";
import { usePermissions } from "@/query/permission";
import { useUpdateOwner, useDeleteMember } from "@/query/member";
import { useScenarioId, useDialog, useModal } from "@/hook";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { errorToast, successToast } from "@/component/Toast";
import HighlightText from "@/component/HighlightText";
import SortButton from "@/component/SortButton";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { MEMBER_MODIFY_MODAL_ID } from "./MemberModifyModal";


interface MemberTableProps {
    searchKeyword?: string;
    members: Member[];
    sortKey: MemberPageParam["sortKey"];
    sortOrder: MemberPageParam["order"];
    onSort: (key: MemberPageParam["sortKey"]) => void;
}

const MemberTable: React.FC<MemberTableProps>  = (props) => {
    const { searchKeyword, members, sortKey, sortOrder, onSort } = props; 

    const intl = useIntl();
    const scenarioId = useScenarioId();
    const modal = useModal();
    const dialog = useDialog();

    const { data: roles } = useRole(scenarioId);
    const { data: userPermissions } = usePermissions(scenarioId);
    const { mutateAsync: deleteMember } = useDeleteMember(scenarioId);
    const { mutateAsync: updateOwner } = useUpdateOwner(scenarioId);

    const onClickDelete = (member: Member) => {
        dialog.open({
            title: intl.formatMessage({ id: "i11012" }),
            content: intl.formatMessage({ id: "i11013" }),
            variant: "danger",
            onConfirm: async () => {
                await deleteMember(member._id)
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }),);
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    const onClickUpdateOwner = (newOwner: Member) => {
        const owner = members.find(member => member.is_admin);
        if (!owner){
            return;
        }
        const username = newOwner.user.username;
        const form: UpdateOwner = {
            owner_member_id:owner._id,
            new_owner_member_id: newOwner._id,
            role_id: roles![0].id,
            management_unit_role_id: undefined
        };

        dialog.open({
            title: intl.formatMessage({ id: "i11002" }),
            content: (intl.formatMessage({ id: "i11017" }, { username })),
            variant: "primary",
            size: "md",
            onConfirm: async () => {
                await updateOwner(form)
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t003" }),);
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t004" }));
                    });
            }
        });
    };

    return (<>
        <div role="table"
             aria-label={intl.formatMessage({ id: "m09" })}
             className="selection-table"
        >
            <div role="rowgroup"
                 className="selection-table-thead grid-table-member"
            >
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "i11004" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "b0021" })}
                </div>
                <div role="columnheader"
                     aria-sort={sortKey === "department" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}
                     className="selection-table-th group pl-[25px]"
                >
                    <SortButton order={sortOrder}
                                active={sortKey === "department"}
                                onClick={() => onSort("department")}
                    >
                        {intl.formatMessage({ id: "i11005" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     aria-sort={sortKey === "member_type" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}
                     className="selection-table-th group pl-[25px]"
                >
                    <SortButton order={sortOrder}
                                active={sortKey === "member_type"}
                                onClick={() => onSort("member_type")}
                    >
                        {intl.formatMessage({ id: "i11006" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "i11007" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {members.map(member => (
                    <div key={member.user_id}
                         role="row"
                         className="selection-table-tr group grid-table-member"
                    >
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            <HighlightText query={member.user.username}
                                           keyword={searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            <HighlightText query={member.user.nickname}
                                           keyword={searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            <HighlightText query={member.user.department || "-"}
                                           keyword={searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            {member.is_admin
                                ? intl.formatMessage({ id: "b0051" })
                                : member.roles.map(role => role.description).join(", ")
                            }
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px] number-cell"
                        >
                            {member.updated_at
                                ? UTCToLocalString(member.updated_at, "YYYY-MM-DD HH:mm")
                                : "-"
                            }
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            {!member.is_admin && (
                                <Popover>
                                    {({ open, close }) => (
                                        <>
                                            <PopoverButton className="selection-table-popover-btn"
                                                           open={open}
                                                           close={close}
                                                           aria-label={`${member.user.username} ${intl.formatMessage({ id: "b0063" })}`}
                                            />
                                            <PopoverMenu>
                                                <PopoverItem className="selection-table-popover-item"
                                                            onClick={() => modal.open(MEMBER_MODIFY_MODAL_ID, member)}
                                                >
                                                    <EditIcon aria-hidden="true"
                                                              className="size-3.5"
                                                    />
                                                    {intl.formatMessage({ id: "i11014" })}
                                                </PopoverItem>
                                                <PopoverItem className="selection-table-popover-item text-point-red"
                                                            onClick={() => onClickDelete(member)}
                                                >
                                                    <DeleteIcon aria-hidden="true"
                                                                className="size-3.5"
                                                    />
                                                    {intl.formatMessage({ id: "i11012" })}
                                                </PopoverItem>
                                                {userPermissions && hasAdminPermission(userPermissions) && (
                                                    <PopoverItem className="selection-table-popover-item"
                                                                onClick={() => onClickUpdateOwner(member)}
                                                    >
                                                        <EditIcon aria-hidden="true"
                                                                  className="size-3.5"
                                                        />
                                                        {intl.formatMessage({ id: "i11002" })}
                                                    </PopoverItem>
                                                )}
                                            </PopoverMenu>
                                            </>
                                        )}
                                </Popover>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </>);
};

export default MemberTable;
